/**
 * The translations read side by side (see the API's TranslationSources).
 * Each language's main one comes with every ayah; the others are fetched per surah.
 */
export const TRANSLATIONS = [
  { id: 'korkut', lang: 'bs', builtIn: true },
  { id: 'mehanovic', lang: 'bs' },
  { id: 'rowwad-bs', lang: 'bs' },
  { id: 'saheeh', lang: 'en', builtIn: true },
  { id: 'hilali-khan', lang: 'en' },
  { id: 'rowwad-en', lang: 'en' },
  { id: 'bubenheim', lang: 'de', builtIn: true },
  { id: 'rowwad-de', lang: 'de' },
  { id: 'rowwad-nl', lang: 'nl', builtIn: true },
  { id: 'rashid', lang: 'fr', builtIn: true },
  { id: 'noor-fr', lang: 'fr' },
];

export const EXTRA_TRANSLATIONS = TRANSLATIONS.filter((t) => !t.builtIn).map((t) => t.id);

/** "the Lord[1] of the worlds[2]" → [{ text: 'the Lord' }, { mark: '1' }, …] */
export function withFootnoteMarks(text) {
  const out = [];
  const re = /\[(\d+)\]/g;
  let last = 0;
  let m;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push({ text: text.slice(last, m.index) });
    out.push({ mark: m[1] });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
}

// Words compare without case, punctuation or footnote marks: "Allah," and "Allah" are the same word.
const comparable = (word) => word.toLocaleLowerCase().replace(/\[\d+\]/g, '').replace(/[^\p{L}\p{N}]/gu, '');

/**
 * Word by word, what `other` says differently from `base`: each of other's
 * words is `same` or `added`, and base's words it left out come in between as
 * `removed`. A longest-common-subsequence diff — ayat are short enough.
 *
 * @returns {{ text: string, kind: 'same'|'added'|'removed' }[]}
 */
export function wordDiff(base, other) {
  const a = base.split(/\s+/).filter(Boolean);
  const b = other.split(/\s+/).filter(Boolean);
  const ka = a.map(comparable);
  const kb = b.map(comparable);

  const rows = a.length + 1;
  const cols = b.length + 1;
  const lcs = new Uint16Array(rows * cols);
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i * cols + j] = ka[i] === kb[j] ? lcs[(i + 1) * cols + j + 1] + 1 : Math.max(lcs[(i + 1) * cols + j], lcs[i * cols + j + 1]);
    }
  }

  const out = [];
  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && ka[i] === kb[j]) {
      out.push({ text: b[j], kind: 'same' });
      i++;
      j++;
    } else if (j < b.length && (i >= a.length || lcs[i * cols + j + 1] >= lcs[(i + 1) * cols + j])) {
      out.push({ text: b[j], kind: 'added' });
      j++;
    } else {
      out.push({ text: a[i], kind: 'removed' });
      i++;
    }
  }
  return out;
}

/** How alike two translations are, 0…1: the words they share over the words both have (Dice). */
export function similarity(diff) {
  const same = diff.filter((d) => d.kind === 'same').length;
  const words = diff.length + same;
  return words ? (2 * same) / words : 1;
}
